/**
 * Bitrate is derived from the delta of `bytesReceived` between two samples, so it
 * needs to remember the previous sample. Prefer {@link createBitrateCalculator} —
 * it keeps that state per-instance, so independent consumers (the live chart and the
 * headless metrics collector) can't corrupt each other's deltas.
 */
export const createBitrateCalculator = () => {
  let previousBytesReceived = 0;
  let previousTimestamp = 0;

  return (stats: { bytesReceived?: number }): number => {
    const now = Date.now();
    const { bytesReceived } = stats;

    if (bytesReceived === undefined || Number.isNaN(bytesReceived)) {
      return 0;
    }

    if (previousBytesReceived && previousTimestamp) {
      const bytesDelta = bytesReceived - previousBytesReceived;
      const timeDelta = now - previousTimestamp;

      // Guard against a zero/negative time delta (e.g. two samples in the same ms).
      const bitrate = timeDelta > 0 ? (bytesDelta * 8) / timeDelta : 0; // bits per ms

      previousBytesReceived = bytesReceived;
      previousTimestamp = now;
      return bitrate * 1000; // bits per second
    }

    previousBytesReceived = bytesReceived;
    previousTimestamp = now;
    return 0;
  };
};

// Backwards-compatible module-level singleton for existing callers.
// New code should use createBitrateCalculator() to get isolated state.
const defaultCalculator = createBitrateCalculator();
export const calculateBitrate = (stats: { bytesReceived?: number }): number =>
  defaultCalculator(stats);
